import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import {
    FaTrash, FaEye, FaSearch, FaUserTie, FaList, FaShareAlt,
    FaTimes, FaBriefcase, FaStar, FaCheckCircle, FaClock, FaTools, FaLayerGroup
} from 'react-icons/fa';
import '../pages_premium.css';
import './HirematesList.css';
import { getFrontendBaseUrl } from '../utils/url';
import AdminShareModal from '../components/common/AdminShareModal';
import AdminPagination from '../components/common/AdminPagination';

function HirematesList() {
    const navigate = useNavigate();
    const [hiremates, setHiremates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [shareItem, setShareItem] = useState(null);
    const [detailModal, setDetailModal] = useState(null); // { item, section, title }
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const handleShareCandidate = (item) => {
        const frontendUrl = getFrontendBaseUrl();
        const name = item.name || item.Provider?.name || item.User?.name || 'Hiremate Candidate';
        setShareItem({
            url: `${frontendUrl}/hiremate?worker=${item.id}`,
            title: name,
            category: 'Hiremate Candidate',
            price: item.hourly_rate ? `$${item.hourly_rate}/hr` : '',
            location: item.preferred_location || item.location || '',
            image: item.profile_photo || item.profile_image || item.Provider?.avatar || item.User?.avatar || '',
            description: item.bio || ''
        });
    };

    const fetchHiremates = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/admin/hiremates');
            setHiremates(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Error fetching hiremates:', error);
            toast.error('Failed to fetch hiremate profiles');
            setHiremates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHiremates();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this Hiremate profile?")) {
            try {
                await axiosInstance.delete(`/admin/hiremates/${id}`);
                toast.success('Hiremate profile deleted successfully');
                fetchHiremates();
            } catch (error) {
                console.error('Error deleting hiremate profile:', error);
                toast.error('Failed to delete profile');
            }
        }
    };

    const parseJSON = (val, fallback = []) => {
        if (!val) return fallback;
        if (typeof val === 'object') return val;
        try {
            return JSON.parse(val);
        } catch (e) {
            return fallback;
        }
    };

    const extractList = (val) => {
        const parsed = parseJSON(val, Array.isArray(val) ? val : (typeof val === 'string' && val ? [val] : []));
        const rawList = Array.isArray(parsed)
            ? parsed
            : (typeof parsed === 'object' && parsed ? Object.keys(parsed) : [String(parsed)]);
        return (rawList || [])
            .map(item => (typeof item === 'object' ? (item.title || item.name || JSON.stringify(item)) : String(item)))
            .map(s => s.trim())
            .filter(Boolean);
    };

    const formatLabel = (str) => {
        if (!str) return '';
        const s = String(str);
        if (s.includes('_')) {
            return s
                .split('_')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
        }
        return s;
    };

    const extractExperienceItems = (exp) => {
        const expObj = parseJSON(exp, exp);
        if (!expObj) return [];
        if (typeof expObj === 'string') {
            return expObj.trim() ? [{ label: 'Summary', value: expObj.trim() }] : [];
        }
        if (Array.isArray(expObj)) {
            return expObj
                .map((e, idx) => {
                    if (typeof e === 'object' && e !== null) {
                        const role = e.role || e.title || `Role ${idx + 1}`;
                        const dur = e.duration || e.years || e.experience || '';
                        return { label: formatLabel(role), value: dur ? String(dur) : 'Listed' };
                    }
                    return { label: `Item ${idx + 1}`, value: String(e) };
                })
                .filter(x => x.value);
        }
        if (typeof expObj === 'object') {
            return Object.entries(expObj).map(([k, v]) => ({
                label: formatLabel(k),
                value: typeof v === 'object' ? JSON.stringify(v) : String(v)
            }));
        }
        return [{ label: 'Experience', value: String(expObj) }];
    };

    const openDetailModal = (item, section, title) => {
        setDetailModal({ item, section, title });
    };

    // Compact cell for Category & Role
    const renderCategoryRoleCompact = (item) => {
        const categories = extractList(item.categories);
        const roles = extractList(item.roles);
        const totalCount = categories.length + roles.length;

        if (totalCount === 0) {
            return <span className="hm-empty-dash">-</span>;
        }

        const firstCategory = categories[0];
        const firstRole = roles[0];
        const shownCount = (firstCategory ? 1 : 0) + (firstRole ? 1 : 0);
        const remainingCount = Math.max(0, totalCount - shownCount);

        return (
            <div className="hm-compact-cell">
                <div className="hm-compact-badges">
                    {firstCategory && (
                        <span className="hm-pill hm-pill-blue" title={formatLabel(firstCategory)}>
                            {formatLabel(firstCategory)}
                        </span>
                    )}
                    {firstRole && (
                        <span className="hm-pill hm-pill-purple" title={formatLabel(firstRole)}>
                            {formatLabel(firstRole)}
                        </span>
                    )}
                </div>
                {(remainingCount > 0 || totalCount > 1) && (
                    <button
                        type="button"
                        className="hm-show-more-btn"
                        onClick={() => openDetailModal(item, 'category_role', 'Categories & Roles')}
                    >
                        {remainingCount > 0 ? `+${remainingCount} Show More` : 'Show More'}
                    </button>
                )}
            </div>
        );
    };

    // Compact cell for Assessment & Score
    const renderAssessmentCompact = (item) => {
        const scores = parseJSON(item.role_scores, {});
        const scoreEntries = typeof scores === 'object' && scores !== null ? Object.entries(scores) : [];
        const overall = item.overall_score || 0;

        return (
            <div className="hm-compact-cell">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                    <span className={`hm-score-badge ${overall >= 80 ? 'hm-score-high' : 'hm-score-mid'}`}>
                        Overall: {overall}%
                    </span>
                </div>
                {scoreEntries.length > 0 && (
                    <>
                        <div className="hm-single-line-preview" title={`${formatLabel(scoreEntries[0][0])}: ${scoreEntries[0][1]}%`}>
                            <strong>{formatLabel(scoreEntries[0][0])}:</strong> {scoreEntries[0][1]}%
                        </div>
                        <button
                            type="button"
                            className="hm-show-more-btn"
                            onClick={() => openDetailModal(item, 'assessment', 'Assessment & Role Scores')}
                        >
                            {scoreEntries.length > 1 ? `+${scoreEntries.length - 1} Show More` : 'Show More'}
                        </button>
                    </>
                )}
            </div>
        );
    };

    // Compact cell for Experience
    const renderExperienceCompact = (item) => {
        const expItems = extractExperienceItems(item.experience || item.experiences);
        if (expItems.length === 0) {
            return <span className="hm-empty-dash">-</span>;
        }

        const first = expItems[0];
        const previewText = first.label === 'Summary' ? first.value : `${first.label}: ${first.value}`;
        const hasMore = expItems.length > 1 || previewText.length > 22;

        return (
            <div className="hm-compact-cell">
                <div className="hm-single-line-preview" title={previewText}>
                    {first.label !== 'Summary' && <strong style={{ color: '#0f172a' }}>{first.label}: </strong>}
                    <span>{first.value}</span>
                </div>
                {hasMore && (
                    <button
                        type="button"
                        className="hm-show-more-btn"
                        onClick={() => openDetailModal(item, 'experience', 'Candidate Experience')}
                    >
                        {expItems.length > 1 ? `+${expItems.length - 1} Show More` : 'Show More'}
                    </button>
                )}
            </div>
        );
    };

    // Compact cell for Skills
    const renderSkillsCompact = (item) => {
        const skills = extractList(item.skills);
        if (skills.length === 0) {
            return <span className="hm-empty-dash">-</span>;
        }

        const firstSkill = skills[0];
        const remaining = skills.length - 1;

        return (
            <div className="hm-compact-cell">
                <div className="hm-compact-badges">
                    <span className="hm-pill hm-pill-green" title={formatLabel(firstSkill)}>
                        {formatLabel(firstSkill)}
                    </span>
                </div>
                {(remaining > 0 || firstSkill.length > 18) && (
                    <button
                        type="button"
                        className="hm-show-more-btn"
                        onClick={() => openDetailModal(item, 'skills', 'Candidate Skills')}
                    >
                        {remaining > 0 ? `+${remaining} Show More` : 'Show More'}
                    </button>
                )}
            </div>
        );
    };

    // Compact cell for Availability
    const renderAvailabilityCompact = (item) => {
        const availList = extractList(item.availability);
        const availText = item.available_now
            ? '⚡ Available Now'
            : (availList.length > 0 ? availList.join(', ') : 'Flexible');
        const hasMore = availList.length > 1 || availText.length > 20 || Boolean(item.preferred_location || item.location || item.bio);

        return (
            <div className="hm-compact-cell">
                <span
                    className={`hm-avail-badge ${item.available_now ? 'hm-avail-now' : ''}`}
                    title={availText}
                >
                    {availText}
                </span>
                {hasMore && (
                    <button
                        type="button"
                        className="hm-show-more-btn"
                        onClick={() => openDetailModal(item, 'availability', 'Availability & Details')}
                    >
                        Show More
                    </button>
                )}
            </div>
        );
    };

    const filtered = hiremates.filter(item => {
        const name = item.Provider?.name || '';
        const email = item.Provider?.email || '';
        const catStr = JSON.stringify(item.categories || '');
        const roleStr = JSON.stringify(item.roles || '');
        const query = search.toLowerCase();
        return name.toLowerCase().includes(query) || email.toLowerCase().includes(query) || catStr.toLowerCase().includes(query) || roleStr.toLowerCase().includes(query);
    });

    const paginatedHiremates = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="page-premium-wrapper hiremates-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaUserTie size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">Hiremate Candidates</h1>
                            <p className="ph-subtitle">Monitor profiles, skill assessments, categories, and availability of all registered Hiremates</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{hiremates.length}</span>
                            <span className="count-lbl">Total Hiremates</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Registered Candidates
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by name, email, category, or role..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        {loading ? (
                            <div className="text-center py-5 text-secondary">Loading Hiremate profiles...</div>
                        ) : (
                            <table className="premium-table hm-fixed-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '52px' }}>#</th>
                                        <th style={{ width: '220px' }}>Candidate / User</th>
                                        <th style={{ width: '185px' }}>Category & Role</th>
                                        <th style={{ width: '180px' }}>Assessment & Score</th>
                                        <th style={{ width: '170px' }}>Experience</th>
                                        <th style={{ width: '160px' }}>Skills</th>
                                        <th style={{ width: '150px' }}>Availability</th>
                                        <th style={{ textAlign: 'center', width: '130px' }}>Total Requests</th>
                                        <th style={{ textAlign: 'center', width: '210px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedHiremates.map((item, index) => {
                                        const user = item.Provider || {};

                                        return (
                                            <tr key={item.id}>
                                                <td style={{ color: '#9ba6c0', fontWeight: 600 }}>#{(currentPage - 1) * pageSize + index + 1}</td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '205px' }}>
                                                        {item.profile_photo || user.avatar ? (
                                                            <img
                                                                src={item.profile_photo || user.avatar}
                                                                alt={user.name}
                                                                style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #cbd5e1', flexShrink: 0 }}
                                                            />
                                                        ) : (
                                                            <div style={{ width: '38px', height: '38px', borderRadius: '50%', backgroundColor: '#0ab68e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '15px', flexShrink: 0 }}>
                                                                {user.name ? user.name.charAt(0).toUpperCase() : 'H'}
                                                            </div>
                                                        )}
                                                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                            <strong className="hm-text-ellipsis" style={{ color: '#0f172a', fontSize: '0.88rem' }} title={user.name || 'Unknown Candidate'}>
                                                                {user.name || 'Unknown Candidate'}
                                                            </strong>
                                                            <span className="hm-text-ellipsis" style={{ fontSize: '11.5px', color: '#64748b' }} title={user.email || '-'}>
                                                                {user.email || '-'}
                                                            </span>
                                                            <span className="hm-text-ellipsis" style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                                {user.phone || '-'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{renderCategoryRoleCompact(item)}</td>
                                                <td>{renderAssessmentCompact(item)}</td>
                                                <td>{renderExperienceCompact(item)}</td>
                                                <td>{renderSkillsCompact(item)}</td>
                                                <td>{renderAvailabilityCompact(item)}</td>
                                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    <span style={{
                                                        backgroundColor: '#e0f2fe',
                                                        color: '#0369a1',
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontWeight: '700',
                                                        fontSize: '12px'
                                                    }}>
                                                        {item.total_requests || 0} Requests
                                                    </span>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <div className="hire-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                                                        <button
                                                            onClick={() => navigate(`/hiremates/${item.id}/requests`)}
                                                            style={{
                                                                backgroundColor: '#4f46e5',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '6px 10px',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontSize: '11.5px',
                                                                fontWeight: '600',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '5px',
                                                                whiteSpace: 'nowrap',
                                                                boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)'
                                                            }}
                                                        >
                                                            <FaList style={{ fontSize: '12px' }} /> Requests
                                                        </button>
                                                        <button
                                                            onClick={() => handleShareCandidate(item)}
                                                            className="action-btn action-btn-share"
                                                            data-tooltip="Share Profile"
                                                            title="Share Candidate Profile"
                                                            style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaShareAlt />
                                                        </button>
                                                        <button
                                                            onClick={() => navigate(`/hiremates/${item.id}`)}
                                                            className="action-btn action-btn-view"
                                                            data-tooltip="View Candidate Profile"
                                                            style={{ backgroundColor: '#0ab68e', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item.id)}
                                                            className="action-btn action-btn-delete"
                                                            data-tooltip="Delete Profile"
                                                            style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {filtered.length === 0 && (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="ptable-empty">
                                                    <div className="ptable-empty-icon">🧑‍🍳</div>
                                                    <p>No Hiremate candidates found.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="candidates"
                    />
                </div>

            </div>

            {/* Attractive Detail Popup Modal for "Show More" */}
            {detailModal && (() => {
                const { item, section } = detailModal;
                const user = item.Provider || {};
                const categories = extractList(item.categories);
                const roles = extractList(item.roles);
                const scores = parseJSON(item.role_scores, {});
                const scoreEntries = typeof scores === 'object' && scores !== null ? Object.entries(scores) : [];
                const expItems = extractExperienceItems(item.experience || item.experiences);
                const skills = extractList(item.skills);
                const availList = extractList(item.availability);

                const tabs = [
                    { id: 'all', label: 'All Details', icon: <FaUserTie size={12} /> },
                    { id: 'category_role', label: 'Category & Role', icon: <FaLayerGroup size={12} /> },
                    { id: 'assessment', label: 'Assessment & Score', icon: <FaStar size={12} /> },
                    { id: 'experience', label: 'Experience', icon: <FaBriefcase size={12} /> },
                    { id: 'skills', label: 'Skills', icon: <FaTools size={12} /> },
                    { id: 'availability', label: 'Availability', icon: <FaClock size={12} /> },
                ];

                const showSection = (secId) => section === 'all' || section === secId;

                return (
                    <div className="hm-modal-backdrop" onClick={() => setDetailModal(null)}>
                        <div className="hm-modal-card" onClick={(e) => e.stopPropagation()}>
                            {/* Modal Header */}
                            <div className="hm-modal-header">
                                <div className="hm-modal-user">
                                    {item.profile_photo || user.avatar ? (
                                        <img
                                            src={item.profile_photo || user.avatar}
                                            alt={user.name}
                                            className="hm-modal-avatar"
                                        />
                                    ) : (
                                        <div className="hm-modal-avatar-placeholder">
                                            {user.name ? user.name.charAt(0).toUpperCase() : 'H'}
                                        </div>
                                    )}
                                    <div className="hm-modal-user-info">
                                        <h3>{user.name || 'Unknown Candidate'}</h3>
                                        <p>{user.email || '-'} {user.phone ? `• ${user.phone}` : ''}</p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span className={`hm-score-badge ${item.overall_score >= 80 ? 'hm-score-high' : 'hm-score-mid'}`} style={{ fontSize: '12.5px', padding: '5px 12px' }}>
                                        Overall Score: {item.overall_score || 0}%
                                    </span>
                                    <button
                                        type="button"
                                        className="hm-modal-close"
                                        onClick={() => setDetailModal(null)}
                                        aria-label="Close modal"
                                    >
                                        <FaTimes size={15} />
                                    </button>
                                </div>
                            </div>

                            {/* Section Filter Tabs */}
                            <div className="hm-modal-tabs">
                                {tabs.map(t => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setDetailModal(prev => ({ ...prev, section: t.id }))}
                                        className={`hm-modal-tab ${section === t.id ? 'active' : ''}`}
                                    >
                                        {t.icon}
                                        <span>{t.label}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Modal Body */}
                            <div className="hm-modal-body">
                                {/* 1. Category & Role */}
                                {showSection('category_role') && (
                                    <div className="hm-detail-block">
                                        <div className="hm-detail-block-title">
                                            <FaLayerGroup style={{ color: '#0284c7' }} />
                                            <span>Categories & Roles ({categories.length + roles.length})</span>
                                        </div>
                                        <div className="hm-detail-subgrid">
                                            <div className="hm-detail-subcard">
                                                <span className="hm-sublabel">Selected Categories ({categories.length})</span>
                                                {categories.length > 0 ? (
                                                    <div className="hm-modal-badge-wrap">
                                                        {categories.map((cat, idx) => (
                                                            <span key={idx} className="hm-pill-full hm-pill-blue">
                                                                {formatLabel(cat)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="hm-empty-text">No categories listed</p>
                                                )}
                                            </div>
                                            <div className="hm-detail-subcard">
                                                <span className="hm-sublabel">Selected Roles ({roles.length})</span>
                                                {roles.length > 0 ? (
                                                    <div className="hm-modal-badge-wrap">
                                                        {roles.map((r, idx) => (
                                                            <span key={idx} className="hm-pill-full hm-pill-purple">
                                                                {formatLabel(r)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="hm-empty-text">No roles listed</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 2. Assessment & Score */}
                                {showSection('assessment') && (
                                    <div className="hm-detail-block">
                                        <div className="hm-detail-block-title">
                                            <FaStar style={{ color: '#eab308' }} />
                                            <span>Assessment & Role Scores</span>
                                        </div>
                                        {scoreEntries.length > 0 ? (
                                            <div className="hm-scores-grid">
                                                {scoreEntries.map(([roleName, pct], idx) => {
                                                    const numPct = Math.min(100, Math.max(0, Number(pct) || 0));
                                                    return (
                                                        <div key={idx} className="hm-score-card">
                                                            <div className="hm-score-card-top">
                                                                <span className="hm-score-role">{formatLabel(roleName)}</span>
                                                                <span className="hm-score-val">{pct}%</span>
                                                            </div>
                                                            <div className="hm-score-bar-bg">
                                                                <div
                                                                    className="hm-score-bar-fill"
                                                                    style={{
                                                                        width: `${Math.max(numPct, 6)}%`,
                                                                        background: numPct >= 75 ? '#16a34a' : numPct >= 40 ? '#f59e0b' : '#3b82f6'
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="hm-empty-text">No individual role scores recorded.</p>
                                        )}
                                    </div>
                                )}

                                {/* 3. Experience */}
                                {showSection('experience') && (
                                    <div className="hm-detail-block">
                                        <div className="hm-detail-block-title">
                                            <FaBriefcase style={{ color: '#f97316' }} />
                                            <span>Work Experience & Bio</span>
                                        </div>
                                        {expItems.length > 0 ? (
                                            <div className="hm-exp-list">
                                                {expItems.map((exp, idx) => (
                                                    <div key={idx} className="hm-exp-item">
                                                        <span className="hm-exp-role">{exp.label}</span>
                                                        <span className="hm-exp-val">{exp.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="hm-empty-text">No experience details listed.</p>
                                        )}
                                        {item.bio && (
                                            <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                                <span className="hm-sublabel" style={{ marginBottom: '4px' }}>Candidate Bio</span>
                                                <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: 1.5 }}>{item.bio}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* 4. Skills */}
                                {showSection('skills') && (
                                    <div className="hm-detail-block">
                                        <div className="hm-detail-block-title">
                                            <FaTools style={{ color: '#16a34a' }} />
                                            <span>Verified Skills ({skills.length})</span>
                                        </div>
                                        {skills.length > 0 ? (
                                            <div className="hm-modal-badge-wrap">
                                                {skills.map((sk, idx) => (
                                                    <span key={idx} className="hm-pill-full hm-pill-green">
                                                        <FaCheckCircle size={10} style={{ marginRight: '5px' }} />
                                                        {formatLabel(sk)}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="hm-empty-text">No skills listed.</p>
                                        )}
                                    </div>
                                )}

                                {/* 5. Availability */}
                                {showSection('availability') && (
                                    <div className="hm-detail-block">
                                        <div className="hm-detail-block-title">
                                            <FaClock style={{ color: '#6366f1' }} />
                                            <span>Availability & Preferences</span>
                                        </div>
                                        <div className="hm-detail-subgrid">
                                            <div className="hm-detail-subcard">
                                                <span className="hm-sublabel">Current Status</span>
                                                <span className={`hm-avail-badge ${item.available_now ? 'hm-avail-now' : ''}`} style={{ maxWidth: 'none' }}>
                                                    {item.available_now ? '⚡ Available Immediately' : 'Standard Schedule'}
                                                </span>
                                            </div>
                                            <div className="hm-detail-subcard">
                                                <span className="hm-sublabel">Preferred Schedule</span>
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                    {availList.length > 0 ? availList.join(', ') : 'Flexible'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="hm-modal-footer">
                                <button
                                    type="button"
                                    className="hm-btn-secondary"
                                    onClick={() => setDetailModal(null)}
                                >
                                    Show Less / Close
                                </button>
                                <button
                                    type="button"
                                    className="hm-btn-primary"
                                    onClick={() => {
                                        const cid = item.id;
                                        setDetailModal(null);
                                        navigate(`/hiremates/${cid}`);
                                    }}
                                >
                                    <FaEye size={13} /> View Full Candidate Profile
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <AdminShareModal
                isOpen={!!shareItem}
                onClose={() => setShareItem(null)}
                {...shareItem}
            />
        </div>
    );
}

export default HirematesList;

